import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Gauge,
  Lightbulb,
  Play,
  Shield,
  Sofa,
  Disc3,
  Wrench,
} from "lucide-react";

const GROUP_ICONS = {
  suspension: Wrench,
  performance: Gauge,
  protection: Shield,
  lighting: Lightbulb,
  brakes: Disc3,
  interior: Sofa,
};

/**
 * Featured build showcase for the home page.
 *
 * The owner wants this build to be the statement piece - "this is what we do".
 * Video on one side, the full spec sheet grouped by system on the other, with
 * every group expandable so 50+ line items never turn into a wall of text.
 * Content comes from settings.featured_build so it stays CMS-editable.
 */
export default function FeaturedBuild({ build, phone }) {
  const [playing, setPlaying] = useState(false);
  const [openGroup, setOpenGroup] = useState(0);

  if (!build?.enabled || !build?.video_id) return null;
  const groups = build.spec_groups || [];

  return (
    <section
      className="relative overflow-hidden border-y"
      style={{ borderColor: "rgba(198,162,74,0.25)", background: "var(--nq-ink)" }}
      data-testid="featured-build"
    >
      <div className="noise-overlay" />
      <div className="container-nq relative section-nq">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          {/* ---------------- video + headline ---------------- */}
          <div className="lg:col-span-7">
            <p className="eyebrow">{build.eyebrow}</p>
            <h2 className="mt-3 text-3xl font-bold leading-[1.08] text-[color:var(--nq-sand)] md:text-5xl">
              {build.headline}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
              {build.standfirst}
            </p>

            <div
              className="relative mt-7 overflow-hidden rounded-xl border"
              style={{ borderColor: "rgba(198,162,74,0.28)" }}
            >
              <div className="aspect-video w-full bg-black">
                {playing ? (
                  <iframe
                    title={build.headline}
                    src={`https://www.youtube.com/embed/${build.video_id}?autoplay=1&rel=0&modestbranding=1`}
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="h-full w-full border-0"
                    data-testid="featured-build-iframe"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    className="group relative h-full w-full"
                    aria-label={`Play video: ${build.headline}`}
                    data-testid="featured-build-play"
                  >
                    <img
                      src={`https://i.ytimg.com/vi/${build.video_id}/maxresdefault.jpg`}
                      alt=""
                      className="h-full w-full object-cover opacity-85 transition-opacity duration-300 group-hover:opacity-100"
                    />
                    <span
                      className="absolute inset-0"
                      style={{ background: "linear-gradient(to top, rgba(11,13,12,0.6), rgba(11,13,12,0.05))" }}
                    />
                    <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform duration-200 group-hover:scale-110"
                      style={{ background: "var(--nq-gold)" }}
                    >
                      <Play className="ml-0.5 h-7 w-7" style={{ color: "var(--nq-black)" }} fill="currentColor" />
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/new-vehicle-build" className="btn-gold" data-testid="featured-build-cta">
                <Wrench className="h-4 w-4" /> {build.cta_label || "Build one like it"}
              </Link>
              <Link to="/our-builds" className="btn-outline-nq" data-testid="featured-build-builds-link">
                See more builds <ArrowRight className="h-4 w-4" />
              </Link>
              {phone && (
                <span className="text-sm text-[color:var(--nq-stone)]">
                  or call{" "}
                  <a
                    href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                    className="font-semibold gold-text hover:opacity-80"
                  >
                    {phone}
                  </a>
                </span>
              )}
            </div>
          </div>

          {/* ---------------- spec sheet ---------------- */}
          <div className="lg:col-span-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="eyebrow">Full build specs</p>
              {build.spec_count > 0 && (
                <span className="font-mono text-sm font-semibold gold-text" data-testid="featured-build-spec-count">
                  {build.spec_count} upgrades
                </span>
              )}
            </div>

            <div className="mt-4 space-y-2" data-testid="featured-build-specs">
              {groups.map((g, i) => {
                const Icon = GROUP_ICONS[g.icon] || Wrench;
                const open = openGroup === i;
                return (
                  <div
                    key={g.title}
                    className="overflow-hidden rounded-lg border"
                    style={{
                      borderColor: open ? "rgba(198,162,74,0.32)" : "rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.02)",
                    }}
                    data-testid="spec-group"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenGroup(open ? -1 : i)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-200 hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--nq-gold)]"
                      aria-expanded={open}
                      data-testid="spec-group-toggle"
                    >
                      <Icon className="h-4 w-4 shrink-0 green-text" />
                      <span className="flex-1 text-sm font-semibold text-[color:var(--nq-sand)]">
                        {g.title}
                      </span>
                      <span className="font-mono text-xs text-[color:var(--nq-stone)]">
                        {(g.items || []).length}
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-[color:var(--nq-stone)] transition-transform duration-200 ${
                          open ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {open && (
                      <ul
                        className="space-y-1.5 border-t px-4 py-3"
                        style={{ borderColor: "rgba(255,255,255,0.07)" }}
                      >
                        {(g.items || []).map((it) => (
                          <li
                            key={it}
                            className="flex gap-2 text-[13px] leading-snug text-[color:var(--nq-stone)]"
                            data-testid="spec-group-item"
                          >
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: "var(--nq-green-bright)" }} />
                            {it}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
